import { useMusic } from "../lib/music";
import { Play, Pause, X } from "lucide-react";

function fmt(sec) {
  if (!sec || !isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Sticky bottom audio bar. Renders only when a track is loaded. Persists
 * across every route change (because <MusicProvider> wraps the router).
 * Layout: album-art · title/artist · scrubber · time · play/pause · close.
 */
export default function MusicPlayerBar() {
  const { track, playing, progress, duration, toggle, stop, seek } = useMusic();
  if (!track) return null;
  return (
    <div
      data-testid="music-bar"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#FF6B00]/30 bg-black/95 backdrop-blur-xl"
      style={{ boxShadow: "0 -10px 40px rgba(255,107,0,0.15)" }}
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 py-2.5 flex items-center gap-3 sm:gap-4">
        {/* Album art */}
        <img
          src={track.art || "/og-fallback.svg"}
          alt=""
          className="w-12 h-12 rounded-md object-cover shrink-0 border border-white/10"
          data-testid="music-bar-art"
        />

        {/* Title + Artist */}
        <div className="min-w-0 flex-1 sm:flex-none sm:w-56">
          <div className="text-sm text-white font-semibold truncate" data-testid="music-bar-title">{track.title || "Untitled"}</div>
          <div className="text-[11px] text-zinc-400 truncate" data-testid="music-bar-artist">{track.artist || "TheSaiyanVictoria"}</div>
        </div>

        {/* Scrubber + time (hidden on tiny screens) */}
        <div className="hidden sm:flex flex-1 items-center gap-3">
          <span className="text-[10px] tabular-nums text-zinc-500 w-10 text-right">{fmt(progress)}</span>
          <input
            type="range"
            min={0}
            max={Math.max(duration || 0, 1)}
            step={0.1}
            value={Math.min(progress, duration || 0)}
            onChange={(e) => seek(parseFloat(e.target.value))}
            data-testid="music-bar-scrubber"
            className="w-full h-1 accent-[#FF6B00] cursor-pointer"
          />
          <span className="text-[10px] tabular-nums text-zinc-500 w-10">{fmt(duration)}</span>
        </div>

        {/* Play / Pause */}
        <button
          data-testid="music-bar-toggle"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="w-10 h-10 rounded-full bg-[#FF6B00] hover:bg-[#FFB800] text-black grid place-items-center transition-colors shrink-0"
        >
          {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
        </button>

        {/* Close */}
        <button
          data-testid="music-bar-close"
          onClick={stop}
          aria-label="Close player"
          className="w-9 h-9 rounded-full text-zinc-500 hover:text-white hover:bg-white/5 grid place-items-center transition-colors shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
